/** Rider-facing labels and colors come from the TripShot catalog, keyed by route ID. */
export function transitRouteIdentity(
  routeId: string | number | null | undefined,
  names: Record<string, string>,
  colors: Record<string, string>,
  fallbackName?: string | null,
) {
  const id = routeId == null ? '' : String(routeId);
  const bare = id.replace(/^ID/i, '');
  const keys = [id, bare, `ID${bare}`];
  const name = keys.map((key) => names[key]).find(Boolean) || fallbackName || (id ? `Route ${bare}` : 'Route unknown');
  const rawColor = keys.map((key) => colors[key]).find(Boolean);
  const color = rawColor && /^#[0-9a-f]{6}$/i.test(rawColor) ? rawColor : '#2563eb';
  return { id: bare || id, name, color };
}

export function busHeadingLabel(bearing: number | null | undefined) {
  if (bearing == null || !Number.isFinite(bearing)) return 'Heading unavailable';
  const degrees = ((Math.round(bearing) % 360) + 360) % 360;
  const compass = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  return `Heading ${compass[Math.round(degrees / 45) % compass.length]} (${degrees}°)`;
}

export function busOccupancy(paxLoad: number | null | undefined, capacity: number | null | undefined) {
  if (paxLoad == null || capacity == null || !Number.isFinite(paxLoad) || !Number.isFinite(capacity) || paxLoad < 0 || capacity <= 0) {
    return { status: 'unknown' as const, color: '#64748b', label: 'Occupancy unavailable' };
  }
  const ratio = paxLoad / capacity;
  const status = ratio < 0.35 ? 'roomy' as const : ratio < 0.8 ? 'filling' as const : 'full' as const;
  const color = status === 'roomy' ? '#16a34a' : status === 'filling' ? '#eab308' : '#dc2626';
  const label = `${paxLoad} of ${capacity} capacity · ${status === 'roomy' ? 'plenty of room' : status === 'filling' ? 'filling up' : 'full or nearly full'}`;
  return { status, color, label };
}

/** A route-colored bus disc with an external heading arrow, following the iOS map grammar. */
export function busMarkerSvg(color: string, routeName: string, bearing: number | null | undefined, paxLoad?: number | null, capacity?: number | null) {
  const safeColor = /^#[0-9a-f]{6}$/i.test(color) ? color : '#2563eb';
  const short = routeName.replace(/\s+Route$/i, '').trim().slice(0, 3).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const arrow = bearing == null || !Number.isFinite(bearing) ? '' : `<path d="M28 2 35 16 28 13 21 16Z" fill="${safeColor}" stroke="#fff" stroke-width="2" transform="rotate(${((bearing % 360) + 360) % 360} 28 29)"/>`;
  const occupancy = busOccupancy(paxLoad, capacity);
  const ring = `<circle cx="28" cy="29" r="22" fill="none" stroke="${occupancy.color}" stroke-width="4"${occupancy.status === 'unknown' ? ' stroke-dasharray="4 3"' : ''}/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">${ring}${arrow}<circle cx="28" cy="29" r="17" fill="${safeColor}" stroke="#fff" stroke-width="2"/><path d="M20 22h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-1v3h-3v-3h-8v3h-3v-3h-1a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Zm2 3v5h12v-5Zm1 7a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm10 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" fill="#fff"/><rect x="33" y="7" width="19" height="15" rx="5" fill="${safeColor}" stroke="#fff" stroke-width="2"/><text x="42.5" y="18" text-anchor="middle" font-family="system-ui,sans-serif" font-size="10" font-weight="700" fill="#fff">${short}</text></svg>`;
}
